import React from 'react'
import { s3Static } from '../../utils/constants'
import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { FaCheckCircle, FaStar, FaShieldAlt, FaUsers, FaRocket, FaHeart, FaLightbulb, FaHandshake, FaChartLine, FaMobileAlt, FaRobot, FaCreditCard, FaComments, FaGlobeAsia } from 'react-icons/fa'
import { MdVerified, MdAir, MdHandyman } from 'react-icons/md'
import { IoBody } from 'react-icons/io5'
import { GiMagicBroom } from 'react-icons/gi'
import { BsStars, BsArrowRight, BsLightningChargeFill } from 'react-icons/bs'
import { HiOutlineSparkles } from 'react-icons/hi'
import ReactCountryFlag from 'react-country-flag'
import { MARKETS, getNavHubLinks, marketBlogsPath } from '../../utils/markets'

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
}

// Company milestones
const milestones = [
  { year: '2018', title: 'Founded', description: 'StringsSG launched as Singapore\'s premier home services platform' },
  { year: '2020', title: 'Platform Growth', description: 'Expanded services across Singapore with vetted professionals' },
  { year: '2024', title: '100K Jobs', description: 'Reached milestone of 100,000+ completed service jobs' },
  { year: '2025', title: 'In-House Trainers & AI', description: 'Launched in-house personal trainers model and RedDotAI technology' },
  { year: '2026', title: 'UAE & Malaysia', description: 'Brought AC servicing and personal trainers to Dubai and Abu Dhabi, and aircond servicing and personal trainers to Kuala Lumpur' },
]

// "Where we operate": one block per country. The section ids are the anchors
// the UAE and Malaysia pages link "About Us" to (aboutPath in utils/markets.js).
// Cities and hub links come from the markets registry; the copy is written per
// country - "AC" in the UAE, "aircond" in Malaysia - and claims nothing those
// markets don't have yet (no in-house trainers, no local office).
const SG_HUB_LINKS = [
  { href: '/sg/aircon-servicing-singapore', label: 'Aircon Servicing Singapore' },
  { href: '/sg/personal-trainer', label: 'Personal Trainer Singapore' },
  { href: '/sg/handyman-singapore-services', label: 'Handyman Services' },
  { href: '/sg/electrician-services-singapore', label: 'Electrician Services' },
  { href: '/sg/part-time-cleaner-singapore', label: 'Part Time Helper' },
]

const countries = [
  {
    countryCd: 'sg',
    id: 'singapore',
    name: 'Singapore',
    places: 'Island-wide',
    since: 'Since 2018',
    body: 'Where StringsSG started. Personal trainers employed directly by StringsSG, aircon servicing with AI SMART Pricing, and handyman, electrician and part-time helper services island-wide.',
    hubs: SG_HUB_LINKS,
    blogs: '/sg/blogs',
  },
  {
    countryCd: 'ae',
    id: 'uae',
    name: 'United Arab Emirates',
    places: MARKETS.ae.cities.map((c) => c.name).join(' · '),
    since: 'Since 2026',
    body: 'AC servicing and certified personal trainers for apartments and villas in Dubai and Abu Dhabi, with prices in dirhams shown before you book. Technicians and trainers are checked before their first job, and every booking runs on the same StringsSG platform and support team as in Singapore.',
    hubs: getNavHubLinks('ae'),
    blogs: marketBlogsPath('ae'),
  },
  {
    countryCd: 'my',
    id: 'malaysia',
    name: 'Malaysia',
    places: `${MARKETS.my.cities.map((c) => c.name).join(' · ')} & Klang Valley`,
    since: 'Since 2026',
    body: 'Aircond servicing and certified personal trainers for condos and landed homes in Kuala Lumpur and the Klang Valley, with prices in ringgit shown before you book. The same checks, service steps and support team as everywhere StringsSG works.',
    hubs: getNavHubLinks('my'),
    blogs: marketBlogsPath('my'),
  },
]

// Core values
const coreValues = [
  {
    icon: FaShieldAlt,
    title: 'Trust & Verification',
    description: 'Everyone who delivers for StringsSG is vetted and background checked, and works to the service steps we define. We don\'t compromise on quality.',
    color: 'from-blue-500 to-indigo-600'
  },
  {
    icon: FaLightbulb,
    title: 'Innovation',
    description: 'From AI-powered pricing to smart booking, we continuously innovate to improve your service experience.',
    color: 'from-amber-500 to-orange-600'
  },
  {
    icon: FaHeart,
    title: 'Customer First',
    description: 'Your satisfaction drives everything we do. Our scoring system ensures only the best professionals serve you.',
    color: 'from-rose-500 to-pink-600'
  },
  {
    icon: FaHandshake,
    title: 'Partner Empowerment',
    description: 'We help service partners grow with zero marketing costs, automated operations, and growth tools.',
    color: 'from-emerald-500 to-teal-600'
  },
]

// Services data
const services = [
  {
    icon: IoBody,
    title: 'Personal Training',
    description: 'In-house certified trainers employed directly by StringsSG for consistent quality and accountability.',
    image: s3Static + 'personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg',
    link: '/sg/personal-trainer',
    trustLink: { href: '/sg/personal-trainer/why-choose', label: 'Why choose StringsSG personal training' },
    highlight: 'In-House Employed',
    color: 'from-rose-500 to-pink-600'
  },
  {
    icon: MdAir,
    title: 'Aircon Servicing',
    description: 'AI-powered SMART Pricing and vetted technicians from licensed contractors, working to StringsSG service standards. Same-day service available.',
    image: s3Static + 'blog/living/aircon/chinese-aircon-service-man-smiling.jpg',
    link: '/sg/aircon-servicing-singapore',
    trustLink: { href: '/sg/aircon-servicing-singapore/how-stringssg-works', label: 'How StringsSG aircon servicing works' },
    highlight: 'AI SMART Pricing',
    color: 'from-sky-500 to-blue-600'
  },
  {
    icon: MdHandyman,
    title: 'Handyman Services',
    description: '24-hour emergency support for plumbing, electrical, carpentry, and home repairs.',
    image: s3Static + 'handyman/handyman-singapore.jpg',
    link: '/sg/handyman-singapore-services',
    trustLink: { href: '/sg/handyman-singapore-services/how-stringssg-works', label: 'How StringsSG handyman services work' },
    highlight: '24/7 Emergency',
    color: 'from-amber-500 to-orange-600'
  },
  {
    icon: GiMagicBroom,
    title: 'Part-Time Helper',
    description: 'Vetted helpers for housekeeping, deep cleaning, and household chores with flexible scheduling.',
    image: s3Static + 'part-time-helper/part-time-helper-in-gray-uniform-living.jpg',
    link: '/sg/part-time-cleaner-singapore',
    trustLink: { href: '/sg/part-time-cleaner-singapore/how-stringssg-works', label: 'How the StringsSG helper service works' },
    highlight: 'Background Verified',
    color: 'from-emerald-500 to-teal-600'
  }
]

// Stats
const stats = [
  { value: '2018', label: 'Founded', icon: FaRocket },
  { value: '100K+', label: 'Services Completed', icon: FaCheckCircle },
  { value: '20K+', label: 'Happy Clients', icon: FaUsers },
  { value: '4.8', label: 'Average Rating', icon: FaStar },
]

export const About = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="-mt-14 pt-14 relative min-h-[70vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src={s3Static + 'girl-with-stringssg-logo-on-mobile.jpg'}
            alt="About StringsSG"
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-linear-to-br from-slate-900/90 via-slate-800/80 to-orange-900/70" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 backdrop-blur-sm border border-orange-400/30 rounded-full px-4 py-2 text-orange-300 text-sm font-medium mb-6">
              <BsStars className="text-lg" />
              <span>Since 2018 · Singapore, UAE &amp; Malaysia</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
              We&apos;re Redefining
              <span className="block bg-linear-to-r from-orange-400 via-amber-400 to-yellow-300 bg-clip-text text-transparent">
                Home Services
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl">
              Not a marketplace — we&apos;re a complete service solution. With StringsSG service standards on
              every job, in-house personal trainers in Singapore, and AI-powered technology, we deliver accountability
              that other platforms simply can&apos;t match, now in Singapore, the UAE and Malaysia.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="bg-linear-to-r from-slate-900 via-slate-800 to-slate-900 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index} className="text-center">
                <stat.icon className="text-2xl text-orange-400 mx-auto mb-2" />
                <div className="text-3xl md:text-4xl font-black text-white mb-1">{stat.value}</div>
                <p className="text-slate-400 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <HiOutlineSparkles />
                OUR STORY
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-6">
                From a Singapore Startup to Three Countries
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  StringsSG was founded in 2018 with a simple mission: to revolutionize how 
                  Singaporeans access home services. We saw a fragmented market filled with 
                  unreliable providers and decided to build something better.
                </p>
                <p>
                  Unlike traditional marketplaces that simply connect customers with random 
                  providers, we took a different approach. In Singapore we directly employ our personal
                  trainers, and for every other service we define the service steps and quality 
                  standards ourselves, vet everyone who delivers them, and take responsibility for the quality.
                </p>
                <p>
                  In October 2025, we launched <strong>RedDotAI</strong>, our proprietary AI 
                  model designed to enhance customer service and power our SMART pricing 
                  algorithms. This technology enables real-time cost calculations and 
                  intelligent service matching.
                </p>
                <p>
                  In 2026 we took the same approach to the UAE and Malaysia: AC servicing and
                  personal trainers in Dubai and Abu Dhabi, and aircond servicing and personal
                  trainers in Kuala Lumpur, priced in local currency and run on the same platform.
                </p>
              </div>
            </div>

            {/* Timeline */}
            <div className="relative">
              <div className="space-y-6">
                {milestones.map((milestone, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 bg-linear-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg">
                        {milestone.year.slice(-2)}
                      </div>
                      {index < milestones.length - 1 && (
                        <div className="w-0.5 h-full bg-orange-200 mt-2" />
                      )}
                    </div>
                    <div className="pb-8">
                      <h3 className="font-bold text-slate-900">{milestone.title}</h3>
                      <p className="text-sm text-slate-500">{milestone.year}</p>
                      <p className="text-slate-600 mt-1">{milestone.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <FaHeart />
              OUR VALUES
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              What Drives Us Every Day
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our core values shape every decision we make and every service we deliver.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {coreValues.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className={`w-14 h-14 bg-linear-to-br ${value.color} rounded-xl flex items-center justify-center mb-5`}>
                  <value.icon className="text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <BsLightningChargeFill />
              WHY STRINGSSG
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              More Than a Marketplace
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We&apos;re a complete service solution with accountability that other platforms can&apos;t match.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: FaUsers,
                title: 'In-House Trainers',
                description: 'In Singapore, personal trainers are directly employed by StringsSG — not freelancers. This ensures consistent quality and accountability.'
              },
              {
                icon: MdVerified,
                title: 'StringsSG Service Standards',
                description: 'We define the service steps and quality standards for every service, vet everyone who delivers them, and take responsibility for the quality - so every customer gets the same service.'
              },
              {
                icon: FaRobot,
                title: 'RedDotAI Technology',
                description: 'Our proprietary AI powers smart pricing, intelligent matching, and enhanced customer support.'
              },
              {
                icon: FaChartLine,
                title: 'Performance Scoring',
                description: 'Our data-driven scoring system ensures only high-performing professionals serve you.'
              },
              {
                icon: FaMobileAlt,
                title: 'One-Stop Platform',
                description: 'Book, pay, track, and manage all services in one place with digital receipts and service records.'
              },
              {
                icon: FaCreditCard,
                title: 'Secure Payments',
                description: 'Where you pay online, payments are TLS 1.3 encrypted: cards, Apple Pay and Google Pay, plus PayNow in Singapore.'
              },
            ].map((feature, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <feature.icon className="text-2xl text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where We Operate */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <FaGlobeAsia />
              WHERE WE OPERATE
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              One Company, Three Countries
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              StringsSG Pte Ltd runs every booking itself, wherever you are. Each country has its own services, prices and pages.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {countries.map((country) => (
              <div key={country.id} id={country.id} className="scroll-mt-20 flex flex-col bg-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4">
                  <ReactCountryFlag
                    countryCode={country.countryCd.toUpperCase()}
                    svg
                    style={{ width: '2.5em', height: '1.9em' }}
                    className="rounded-md shadow"
                    aria-hidden="true"
                  />
                  <div>
                    <h3 className="text-xl font-black text-slate-900 leading-tight">{country.name}</h3>
                    <p className="text-sm text-slate-500">{country.places} · {country.since}</p>
                  </div>
                </div>
                <p className="mt-4 text-slate-600 text-sm leading-relaxed">{country.body}</p>
                <ul className="mt-4 space-y-2 flex-1">
                  {country.hubs.map((hub) => (
                    <li key={hub.href}>
                      <Link href={hub.href} prefetch={false} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800 hover:text-orange-600">
                        <MdVerified className="shrink-0 text-orange-500" aria-hidden="true" />
                        {hub.label}
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                  <Link href={MARKETS[country.countryCd].homePath} prefetch={false} className="inline-flex items-center gap-1.5 font-semibold text-orange-600 hover:underline">
                    StringsSG {MARKETS[country.countryCd].shortName}
                    <BsArrowRight />
                  </Link>
                  <Link href={country.blogs} prefetch={false} className="font-semibold text-slate-500 hover:text-orange-600">
                    Guides
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services (Singapore) */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <MdVerified />
              OUR SERVICES IN SINGAPORE
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
              Premium Services, Verified Professionals
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
              >
                <Link href={service.link} className="group block">
                  <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="relative h-60 overflow-hidden">
                      <Image
                        src={service.image}
                        alt={service.title}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span className={`inline-flex items-center gap-1.5 bg-linear-to-r ${service.color} text-white text-xs font-bold px-3 py-1.5 rounded-full`}>
                          <MdVerified />
                          {service.highlight}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 bg-linear-to-br ${service.color} rounded-xl flex items-center justify-center`}>
                          <service.icon className="text-xl text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900">{service.title}</h3>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                    </div>
                  </div>
                </Link>
                {/* Segment trust page: how this service actually works. */}
                {service.trustLink && (
                  <Link href={service.trustLink.href} prefetch={false} className="inline-block mt-3 ml-1 text-sm text-orange-600 hover:underline">
                    {service.trustLink.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-linear-to-br from-orange-500 via-orange-600 to-amber-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="space-y-6">
            <h2 className="text-3xl md:text-4xl font-black text-white">
              Ready to Experience the Difference?
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Join thousands of satisfied customers who trust StringsSG for their home service needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Link
                href="/signup"
                className="inline-flex items-center justify-center gap-2 bg-white text-orange-600 font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Get Started
                <BsArrowRight />
              </Link>
              <Link
                href="/service-provider-signup"
                className="inline-flex items-center justify-center gap-2 bg-white/10 border-2 border-white/30 text-white font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 hover:bg-white/20"
              >
                Become a Partner
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
